import React from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { formatBillingSourceType, formatLineItemType, formatMoney, getLineItemDisplayTotal } from "@/utils/billingFormat";

const BillLineItemsTable = ({ items = [], currencyCode = "NPR" }) => {
  if (!items.length) {
    return (
      <div className="rounded-2xl border border-dashed p-8 text-center text-sm text-muted-foreground">
        No line items added yet.
      </div>
    );
  }

  return (
    <>
      <div className="hidden overflow-hidden rounded-2xl border bg-background md:block">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Type</TableHead>
              <TableHead>Description</TableHead>
              <TableHead className="text-right">Quantity</TableHead>
              <TableHead className="text-right">Unit price</TableHead>
              <TableHead className="text-right">Discount</TableHead>
              <TableHead className="text-right">Tax</TableHead>
              <TableHead className="text-right">Service charge</TableHead>
              <TableHead className="text-right">Line total</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.map((item) => (
              <TableRow key={item.id || `${item.description}-${item.lineTotal}`}>
                <TableCell>{formatLineItemType(item.itemType)}</TableCell>
                <TableCell>
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="font-medium">{item.description}</p>
                    <LineOriginBadge item={item} />
                    {!item.affectsDueAmount && (
                      <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-medium text-emerald-700">
                        Informational
                      </span>
                    )}
                  </div>
                  {(item.revenueAccountName || item.revenueAccountCode) && (
                    <p className="mt-1 text-xs text-muted-foreground">
                      {item.revenueAccountName || item.revenueAccountCode}
                    </p>
                  )}
                  {(item.sourceKey || item.sourceType) && (
                    <p className="mt-1 text-xs text-muted-foreground">
                      Source: {formatBillingSourceType(item.sourceType)}
                      {item.sourceKey ? ` · ${item.sourceKey}` : ""}
                    </p>
                  )}
                  {item.isInformational && (
                    <p className="mt-1 text-xs font-medium text-emerald-700">
                      Paid directly at POS. Not included in the due amount.
                    </p>
                  )}
                </TableCell>
                <TableCell className="text-right">{item.quantity.toFixed(2)}</TableCell>
                <TableCell className="text-right">
                  {formatMoney(item.unitPrice, currencyCode)}
                </TableCell>
                <TableCell className="text-right">
                  {formatMoney(item.discountAmount, currencyCode)}
                </TableCell>
                <TableCell className="text-right">
                  {formatMoney(item.taxAmount, currencyCode)}
                </TableCell>
                <TableCell className="text-right">
                  {formatMoney(item.serviceChargeAmount, currencyCode)}
                </TableCell>
                <TableCell className="text-right font-semibold">
                  {getLineItemDisplayTotal(item, currencyCode)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <div className="space-y-3 md:hidden">
        {items.map((item) => (
          <div
            key={item.id || `${item.description}-${item.lineTotal}`}
            className="rounded-2xl border bg-background p-4"
          >
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="text-xs font-medium text-muted-foreground">
                  {formatLineItemType(item.itemType)}
                </p>
                <p className="mt-1 font-semibold">{item.description}</p>
                <div className="mt-2 flex flex-wrap gap-2">
                  <LineOriginBadge item={item} />
                  {!item.affectsDueAmount && (
                    <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-medium text-emerald-700">
                      Informational
                    </span>
                  )}
                </div>
              </div>
              <p className="shrink-0 font-semibold">
                {getLineItemDisplayTotal(item, currencyCode)}
              </p>
            </div>
            {item.isInformational && (
              <p className="mt-2 text-xs font-medium text-emerald-700">
                Paid directly at POS. Not included in the due amount.
              </p>
            )}
            {(item.sourceKey || item.sourceType) && (
              <p className="mt-2 break-all text-xs text-muted-foreground">
                Source: {formatBillingSourceType(item.sourceType)}
                {item.sourceKey ? ` · ${item.sourceKey}` : ""}
              </p>
            )}
            <div className="mt-3 grid grid-cols-2 gap-2 text-sm text-muted-foreground">
              <span>Quantity {item.quantity.toFixed(2)}</span>
              <span>Unit price {formatMoney(item.unitPrice, currencyCode)}</span>
              <span>Discount {formatMoney(item.discountAmount, currencyCode)}</span>
              <span>Tax {formatMoney(item.taxAmount, currencyCode)}</span>
            </div>
          </div>
        ))}
      </div>
    </>
  );
};

const LineOriginBadge = ({ item }) => {
  const generated = Boolean(item.isGenerated || item.sourceKey);
  return (
    <span className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${generated ? "bg-blue-50 text-blue-700" : "bg-slate-100 text-slate-700"}`}>
      {generated ? "Generated" : "Manual"}
    </span>
  );
};

export default BillLineItemsTable;
